import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import SectionCard from '../../components/admin/SectionCard';
import { LoadingState } from '../../components/admin/StateViews';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { ChallengeService } from '../../services/ChallengeService';
import { AdminChallengeDetail, ChallengeStatus } from '../../types';

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const formatAmount = (value: string | number) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });

const STATUS_COLORS: Record<ChallengeStatus, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  COMPLETED: 'default',
  EXPIRED: 'warning',
  CANCELLED: 'error',
};

const ChallengeDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canDelete = user?.role === 'SuperAdmin';

  const [challenge, setChallenge] = useState<AdminChallengeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setChallenge(await ChallengeService.getChallengeById(id));
      } catch {
        setError('Failed to load challenge.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await ChallengeService.deleteChallenge(id);
      showToast('Challenge deleted.', 'success');
      navigate('/challenges');
    } catch {
      showToast('Failed to delete challenge.', 'error');
    } finally {
      setConfirmDelete(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error || !challenge) {
    return (
      <AdminPage title="Challenge Details">
        <Alert severity="error">{error ?? 'Challenge not found.'}</Alert>
      </AdminPage>
    );
  }

  const progressValue = Math.min(challenge.percentage, 100);

  return (
    <AdminPage
      title={challenge.title}
      description={`Challenge · ${challenge.status}`}
      action={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(`/challenges/${id}/edit`)}>
            Edit
          </Button>
          {canDelete && !challenge.deletedAt && (
            <Button variant="outlined" color="error" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
        </Stack>
      }
    >
      <SectionCard title="Overview">
        <Stack spacing={1.5}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip label={challenge.status} color={STATUS_COLORS[challenge.status] ?? 'default'} size="small" />
            {challenge.deletedAt && <Chip label="Deleted" color="error" size="small" />}
          </Box>

          <Typography variant="body2" color="text.secondary">
            {challenge.description}
          </Typography>

          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Cause ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{challenge.causeId}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Creator</Typography>
              <Typography variant="body2">{challenge.creator.displayName ?? challenge.createdById.slice(0, 8)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Start</Typography>
              <Typography variant="body2">{formatDate(challenge.startDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">End</Typography>
              <Typography variant="body2">{formatDate(challenge.endDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography variant="body2">{formatDate(challenge.createdAt)}</Typography>
            </Box>
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title="Progress">
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary">Raised</Typography>
              <Typography variant="h6" color="primary">
                {challenge.currency} {formatAmount(challenge.actualAmount)}
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary">Target</Typography>
              <Typography variant="h6">
                {challenge.currency} {formatAmount(challenge.targetAmount)}
              </Typography>
            </Box>
          </Stack>
          <LinearProgress variant="determinate" value={progressValue} sx={{ height: 10, borderRadius: 5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {challenge.contributionsCount} contribution{challenge.contributionsCount !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="primary">
              {Math.min(Math.round(challenge.percentage), 100)}%
            </Typography>
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title={`Contributions (${challenge.contributions.length})`}>
        {challenge.contributions.length === 0 ? (
          <Typography color="text.secondary">No contributions yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Donor</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Donation ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {challenge.contributions.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    {c.isAnonymous ? (
                      <em style={{ color: '#888' }}>Anonymous</em>
                    ) : (
                      c.displayName ?? c.userId.slice(0, 8)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {c.currency} {formatAmount(c.amount)}
                  </TableCell>
                  <TableCell>{formatDate(c.createdAt)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.donationId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Challenge"
        description={`Are you sure you want to delete "${challenge.title}"? Existing donations and user points will NOT be affected.`}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </AdminPage>
  );
};

export default ChallengeDetailsPage;
